import { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase/config';
import { useAuth } from './useAuth';

export interface SocialMediaPlatformStatus {
  enabled: boolean;
  hasCredentials: boolean;
}

export interface SocialMediaConfigStatus {
  environment: string;
  testMode: boolean;
  platforms: {
    facebook: SocialMediaPlatformStatus;
    twitter: SocialMediaPlatformStatus;
    instagram: SocialMediaPlatformStatus;
  };
}

export const useSocialMediaConfig = () => {
  const { user, isAdmin } = useAuth();
  const [config, setConfig] = useState<SocialMediaConfigStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize Firebase Function
  const getSocialMediaConfigStatusFunction = useMemo(() => 
    httpsCallable(functions, 'getSocialMediaConfigStatus'), []
  );

  useEffect(() => {
    if (!user || !isAdmin) {
      setLoading(false);
      return;
    }

    const fetchConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await getSocialMediaConfigStatusFunction();
        const data = result.data as { success: boolean; data?: SocialMediaConfigStatus; error?: string };
        
        if (data.success && data.data) {
          setConfig(data.data);
        } else {
          throw new Error(data.error || 'Failed to fetch configuration');
        }
      } catch (err) {
        console.error('Error fetching social media config:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Fallback to default configuration
        setConfig({
          environment: 'development',
          testMode: true,
          platforms: {
            facebook: { enabled: true, hasCredentials: false },
            twitter: { enabled: true, hasCredentials: false },
            instagram: { enabled: true, hasCredentials: false },
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [user, isAdmin, getSocialMediaConfigStatusFunction]);

  return {
    config,
    loading,
    error,
    refetch: () => {
      if (user && isAdmin) {
        setLoading(true);
        // Re-trigger the effect by updating a dependency
      }
    },
  };
};